import { useId, useState } from "react";

import type { MultiLegDraft } from "../../lib/multiLegPosition";
import type { WatchOptionEvidenceBaseSubject } from "../../lib/watchOptionEvidenceCapabilities";
import type { WatchOptionEvidenceFormDraft } from "../../lib/watchOptionEvidenceDraft";
import type { WatchSignalDefinition } from "../../lib/watchSignalCatalog";
import { canonicalRatioToPercent, percentToCanonicalRatio } from "../../lib/watchRatioPresentation";
import {
  getReviewedSignalPresenter,
  type ExactOptionContractMeasurement,
  type MovingAverageTriggerMode,
} from "../../lib/watchSignalReleaseRegistry";
import styles from "./WatchOptionConditionFields.module.css";

type OptionConditionFamily = "VOLATILITY" | "LIQUIDITY";

export type WatchOptionConditionFieldsValue = WatchOptionEvidenceFormDraft;

export interface WatchOptionConditionFieldsProps {
  baseSubject: WatchOptionEvidenceBaseSubject;
  definitions: readonly WatchSignalDefinition[];
  position: MultiLegDraft;
  value: WatchOptionConditionFieldsValue;
  onChange: (value: WatchOptionConditionFieldsValue) => void;
  invalid?: boolean;
  errorMessageId?: string;
}

interface ReviewedDefinition {
  definition: WatchSignalDefinition;
  measurement: ExactOptionContractMeasurement;
  triggerMode: MovingAverageTriggerMode;
}

const FAMILY_TITLES: Readonly<Record<OptionConditionFamily, string>> = {
  VOLATILITY: "Contract volatility",
  LIQUIDITY: "Contract liquidity",
};

const MEASUREMENT_COPY: Readonly<Record<ExactOptionContractMeasurement, string>> = {
  IMPLIED_VOLATILITY: "Implied volatility",
  NBBO_RELATIVE_SPREAD: "Bid/ask spread",
  REGULAR_SESSION_VOLUME: "Session volume",
  OPEN_INTEREST: "Open interest",
};

function reviewedDefinitions(
  definitions: readonly WatchSignalDefinition[],
): ReviewedDefinition[] {
  return definitions.flatMap((definition) => {
    const presenter = getReviewedSignalPresenter(definition);
    if (presenter?.kind !== "EXACT_OPTION_CONTRACT") return [];
    // Open-interest crossing remains deliberately absent until its dated
    // observation semantics receive an explicit product release.
    if (presenter.measurement === "OPEN_INTEREST"
      && presenter.triggerMode === "CROSSING") return [];
    return [{ definition, measurement: presenter.measurement, triggerMode: presenter.triggerMode }];
  });
}

function formatExpiry(expiry: string) {
  const [year, month, day] = expiry.split("-").map(Number);
  if (!year || !month || !day) return expiry;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function readableContract(position: MultiLegDraft, legIndex: number) {
  const leg = position.legs[legIndex];
  if (!leg) return "Option details unavailable";
  return `${position.underlying.trim().toUpperCase()} · ${formatExpiry(position.expiry)} · $${leg.strike} ${leg.optionType}`;
}

function readableList(items: readonly string[]): string {
  if (items.length < 2) return items[0] ?? "available data";
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, or ${items.at(-1)}`;
}

export default function WatchOptionConditionFields({
  baseSubject,
  definitions,
  position,
  value,
  onChange,
  invalid = false,
  errorMessageId,
}: WatchOptionConditionFieldsProps) {
  const contentId = useId();
  const [expanded, setExpanded] = useState<boolean | null>(null);
  const reviewed = reviewedDefinitions(definitions);
  if (reviewed.length === 0) return null;

  const hasDraft = value.family !== undefined
    || value.measurement !== undefined
    || value.signalId !== undefined;
  const showContent = expanded ?? hasDraft;
  const isSingle = position.structure === "single";
  const hasTarget = isSingle || value.targetLegIndex !== undefined;
  const families = (["VOLATILITY", "LIQUIDITY"] as const).filter(
    (family) => reviewed.some(({ definition }) => definition.family === family),
  );
  const familyDescriptions = Object.fromEntries(families.map((family) => {
    const availableMeasurements = Array.from(new Set(reviewed
      .filter(({ definition }) => definition.family === family)
      .map(({ measurement }) => MEASUREMENT_COPY[measurement].toLowerCase())));
    return [family, `Watch ${readableList(availableMeasurements)} for one option.`];
  })) as Partial<Record<OptionConditionFamily, string>>;
  const measurements = value.family
    ? Array.from(new Set(reviewed
        .filter(({ definition }) => definition.family === value.family)
        .map(({ measurement }) => measurement)))
    : [];
  const modes = value.measurement
    ? reviewed.filter(({ measurement }) => measurement === value.measurement)
    : [];
  const selected = reviewed.find(({ definition }) => (
    definition.signal_id === value.signalId
    && definition.definition_version === value.definitionVersion
  ));
  const isRatio = selected?.definition.unit === "RATIO";

  const updateFamily = (family: OptionConditionFamily) => onChange({
    targetLegIndex: value.targetLegIndex,
    family,
    threshold: "",
  });
  const updateMeasurement = (measurement: ExactOptionContractMeasurement) => {
    const choices = reviewed.filter((item) => item.measurement === measurement);
    const only = choices.length === 1 ? choices[0] : undefined;
    onChange({
      targetLegIndex: value.targetLegIndex,
      family: value.family,
      measurement,
      triggerMode: only?.triggerMode,
      signalId: only?.definition.signal_id,
      definitionVersion: only?.definition.definition_version,
      threshold: "",
    });
  };
  const updateMode = (choice: ReviewedDefinition) => onChange({
    targetLegIndex: value.targetLegIndex,
    family: value.family,
    measurement: value.measurement,
    triggerMode: choice.triggerMode,
    signalId: choice.definition.signal_id,
    definitionVersion: choice.definition.definition_version,
    threshold: "",
  });

  const title = baseSubject === "POSITION_EXIT"
    ? "Add a contract condition"
    : "Add entry context";
  const availableContext = readableList(families.map((family) => (
    family === "VOLATILITY" ? "volatility" : "liquidity"
  )));
  const description = baseSubject === "POSITION_EXIT"
    ? `Require this option’s ${availableContext} to confirm this alert.`
    : `Require this option’s ${availableContext} to confirm your target entry.`;

  return (
    <section className={styles.root} data-option-condition-root="true">
      <button
        type="button"
        className={styles.disclosure}
        aria-expanded={showContent}
        aria-controls={contentId}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorMessageId : undefined}
        onClick={() => setExpanded(!showContent)}
      >
        <span>
          <strong>{title}</strong>
          <small>{description}</small>
        </span>
        <span aria-hidden="true">{showContent ? "−" : "+"}</span>
      </button>

      {showContent ? (
        <div id={contentId} className={styles.content}>
          {isSingle ? (
            <div className={styles.summary}>
              <strong>This option</strong>
              <span>{position.legs[0]?.side === "SHORT" ? "Short" : "Long"} {position.legs[0]?.contracts || "—"} contract{position.legs[0]?.contracts === "1" ? "" : "s"} · {readableContract(position, 0)}</span>
            </div>
          ) : (
            <fieldset className={styles.group}>
              <legend>Which option?</legend>
              <div className={styles.choiceGrid}>
                {position.legs.map((leg, index) => (
                  <label className={styles.choice} key={`${leg.side}-${leg.optionType}-${leg.strike}-${index}`}>
                    <input
                      type="radio"
                      name={`${contentId}-leg`}
                      checked={value.targetLegIndex === index}
                      onChange={() => onChange({ targetLegIndex: index, threshold: "" })}
                    />
                    <span>
                      <strong>{leg.side === "LONG" ? "Long" : "Short"} {leg.optionType}</strong>
                      <small>{readableContract(position, index)}</small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {hasTarget ? (
            <fieldset className={styles.group}>
              <legend>What kind of condition?</legend>
              <div className={styles.choiceGrid}>
                {families.map((family) => (
                  <label className={styles.choice} key={family}>
                    <input
                      type="radio"
                      name={`${contentId}-family`}
                      checked={value.family === family}
                      onChange={() => updateFamily(family)}
                    />
                    <span>
                      <strong>{FAMILY_TITLES[family]}</strong>
                      <small>{familyDescriptions[family]}</small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}

          {value.family ? (
            <fieldset className={styles.group}>
              <legend>What should we measure?</legend>
              <div className={styles.choiceGrid}>
                {measurements.map((measurement) => (
                  <label className={styles.choice} key={measurement}>
                    <input
                      type="radio"
                      name={`${contentId}-measurement`}
                      checked={value.measurement === measurement}
                      onChange={() => updateMeasurement(measurement)}
                    />
                    <span><strong>{MEASUREMENT_COPY[measurement]}</strong></span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}

          {value.measurement && modes.length > 1 ? (
            <fieldset className={styles.group}>
              <legend>How should this condition work?</legend>
              <div className={styles.choiceGrid}>
                {modes.map((choice) => (
                  <label className={styles.choice} key={choice.definition.signal_id}>
                    <input
                      type="radio"
                      name={`${contentId}-mode`}
                      checked={value.signalId === choice.definition.signal_id}
                      onChange={() => updateMode(choice)}
                    />
                    <span>
                      <strong>{choice.triggerMode === "STATE" ? "Is at or beyond a level" : "Newly crosses a level"}</strong>
                      <small>{choice.triggerMode === "STATE"
                        ? "May be true on the first fresh check."
                        : "The first fresh check sets a baseline. Only a later crossing can trigger the alert."}</small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : value.measurement && modes.length === 1 ? (
            <p className={styles.explanation}>{modes[0].triggerMode === "STATE"
              ? "This condition may be true on the first fresh check."
              : "The first fresh check sets a baseline. Only a later crossing can trigger the alert."}</p>
          ) : null}

          {selected ? (
            <fieldset className={styles.group}>
              <legend>Which direction?</legend>
              <div className={styles.inlineChoices}>
                {(["ABOVE", "BELOW"] as const).map((direction) => (
                  <label className={styles.choice} key={direction}>
                    <input
                      type="radio"
                      name={`${contentId}-direction`}
                      checked={value.direction === direction}
                      onChange={() => onChange({ ...value, direction, threshold: "" })}
                    />
                    <span><strong>{direction === "ABOVE" ? "At or above" : "At or below"}</strong></span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}

          {selected && value.direction ? (
            <label className={styles.threshold}>
              <span>{MEASUREMENT_COPY[selected.measurement]}</span>
              <span className={styles.inputRow}>
                <input
                  type="text"
                  inputMode={selected.definition.unit === "COUNT" ? "numeric" : "decimal"}
                  value={isRatio ? canonicalRatioToPercent(value.threshold ?? "") : value.threshold ?? ""}
                  aria-invalid={invalid || undefined}
                  aria-describedby={invalid ? errorMessageId : undefined}
                  onChange={(event) => onChange({
                    ...value,
                    threshold: isRatio ? percentToCanonicalRatio(event.target.value) : event.target.value,
                  })}
                />
                <span>{isRatio ? "%" : "contracts"}</span>
              </span>
              <small>{selected.measurement === "IMPLIED_VOLATILITY"
                ? "45% means an implied volatility of 45%."
                : selected.measurement === "NBBO_RELATIVE_SPREAD"
                  ? "Measured as a percentage of the contract midpoint."
                  : selected.measurement === "REGULAR_SESSION_VOLUME"
                    ? "Contracts reported during this regular session."
                    : "Open contracts from the latest officially dated observation."}</small>
            </label>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
